import { useEffect, useState, type ReactNode } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import { BottomNav, type BottomNavItem } from './BottomNav'
import { Footer, type FooterProps } from './Footer'
import { Header } from './Header'
import { HeaderNav } from './HeaderNav'
import { MobileSidebar } from './MobileSidebar'
import { Preloader, type PreloaderProps } from './Preloader'
import { ScrollToTop } from './ScrollToTop'
import { SearchModal } from './SearchModal'
import type { LinkTarget, NavEntry, SocialLink } from './types'
import { useRouteSeo } from '../seo/useSeo'
import type { SeoConfig } from '../seo/types'
import { useIsMobileNav } from '../hooks/useMediaQuery'
import { usePreloader } from '../hooks/usePreloader'
import { useReveal } from '../hooks/useReveal'

export interface AppShellProps {
  logo: string
  /** The pale lockup, for the bar floating over a photograph — see `Header`'s `logoPale`. */
  logoPale?: string
  /** The venture's name under the mark — see `Header`'s `logoLabel`. */
  logoLabel?: string
  /** This app's own home route. Never another app — that is what the nav is for. */
  homeTo?: string
  nav: NavEntry[]
  /**
   * The off-canvas panel's footer: who to follow, and the other KKF sites.
   *
   * `sites` is this app's three siblings — never itself. The shell cannot work that out,
   * because it does not know which of the four it is; the app composes the list. See
   * `MobileSidebar`'s `sites`.
   */
  sidebar: {
    followTitle: string
    socials: SocialLink[]
    sites?: { label: string; href: string }[]
    sitesTitle?: string
  }
  /** Everything the header needs that is not the nav. */
  header?: {
    cta?: LinkTarget & { label: string }
    /**
     * Routes that open straight into pale content rather than behind a dark banner.
     *
     * The header is absolutely positioned over that first section and its links are white
     * at rest, because the usual first section is a dark photograph. Over anything pale
     * they disappear — so a pale route says so here and the bar darkens.
     *
     * An entry ending in `/*` matches that prefix and everything under it, which is the
     * only way a **dynamic** route can be listed: `/shop/*` covers `/shop/:slug`, which no
     * exact path can. Anything else is an exact match.
     *
     * Pair a route here with `ul-plain-page-top` on its first section **only if that
     * section opens into content** — the class reserves the bar's height, and a page whose
     * first section is a pale-scrimmed photograph already has its own top padding. The two
     * concerns are separate and only look joined because one list used to drive both.
     *
     * ── Why this is a list and not a boolean ────────────────────────────────────────────
     *
     * It was briefly `onLight: boolean` for `app_garage`, on the reasoning that its whole
     * register is pale now and a list is a thing to keep in step with the route table. That
     * was wrong in the most literal way: `/tours/:slug` is the one garage page that still
     * opens on `Breadcrumb`'s dark photographic banner, so an app-wide "on light" rendered
     * ink navy links on a near-black photograph — the exact bug it was written to fix, on
     * one route instead of nine. "Is the bar over something pale" is a per-route fact and
     * always was.
     */
    plainRoutes?: string[]
    /** An extra control in the bar — see `Header`'s `actions`. */
    actions?: ReactNode
    /**
     * Whether the bar carries the search opener. Defaults to true.
     *
     * `false` also stops `SearchModal` being mounted: the opener is the only thing that
     * opens it, so a bar without one leaves a modal in the tree that nothing can reach.
     * The Foundation is the app that passes it — see `Header`'s `showSearch`.
     */
    search?: boolean
  }
  footer: Omit<FooterProps, 'onSubscribe' | 'tone'>
  /**
   * The shell's register. `ink` gives the dark-first app an opaque fixed header, a dark
   * footer, and the top padding on `<main>` that the fixed bar makes necessary — the
   * three of which always travel together, which is why it is one prop on the shell
   * rather than three on the pieces.
   */
  tone?: 'light' | 'ink'
  /** A verse on the loading overlay. See `Preloader` for why it is usually not read. */
  preloaderVerse?: PreloaderProps['verse']
  /**
   * The fixed tab bar under the thumb, below 992px.
   *
   * Optional, so an app that has not chosen its tabs renders exactly as it did before this
   * existed — and the shell adds `.ul-has-bottom-nav` to `<main>` only when there is a bar
   * to make room for, rather than reserving 60px at the foot of an app that has none.
   *
   * `label` is the bar's accessible name and is required by `BottomNav` for the reason
   * given there: a default would be English, and every app mounting this already has a
   * second `<nav>` in the header.
   */
  bottomNav?: {
    label: string
    items: BottomNavItem[]
    /**
     * Given, a final tab is appended that opens the off-canvas.
     *
     * It is here rather than an `onClick` in `items` because the panel's open state belongs
     * to this shell — an app composing the list has no way to reach it. The Partners builds
     * the equivalent tab itself for the same reason in reverse: `MarketShell` owns its own
     * drawer.
     *
     * It matters more than it looks. The hamburger is pinned to the top-right corner, which
     * is the furthest point on a phone from a right thumb; without this tab the whole nav is
     * the one thing on the screen a one-handed reader cannot reach.
     */
    menuLabel?: string
  }
  /** Class for the `<main>` wrapper — set from the route tree, not per page. */
  mainClassName?: string
  /**
   * This app's site tree, so the shell can set each route's metadata as a visitor
   * navigates. Omitted, the shell writes no metadata at all and the page in the tab keeps
   * whatever title the last one set.
   *
   * Pass a **module-level constant**, not an object literal in the render body: the
   * lookup index is memoised on this object's identity, and a fresh literal every render
   * rebuilds it every render.
   *
   * Only an exact path match is applied: a route the tree could not enumerate is left to
   * its own `useSeo`. A route that IS in the tree can still refine what the tree says
   * about it — `/book` marks a filtered result set `noindex` — because the shell writes in
   * a layout effect and a page writes in a passive one. See `useRouteSeo`.
   */
  seo?: SeoConfig
  onSubscribe?: (email: string) => void
  onSearch?: (query: string) => void
}

/**
 * The shell every KKF app mounts around its routes: preloader, off-canvas sidebar,
 * search modal, header, page outlet, footer, back-to-top.
 *
 * It lives in the design system rather than in an app because four apps need the same
 * one, and a shared block gets defined once — the same rule that put `Header` and
 * `Footer` here. What varies between apps is *content* (nav entries, footer copy,
 * theme), and all of it arrives as props.
 *
 * Two things here are less obvious than they look:
 *
 * 1. **Nav placement.** The single `.to-go-to-sidebar-in-mobile` node belongs to the
 *    header above 992px and to the sidebar below it. We render it into whichever
 *    container is correct instead of moving the node imperatively. It must be in exactly
 *    one of the two places, never both.
 * 2. **Route changes.** The sidebar and search overlay must close on navigation, the page
 *    must scroll to top, and the reveal observers must re-arm against the new page's
 *    `.wow` elements.
 */
export function AppShell({
  logo,
  logoPale,
  logoLabel,
  homeTo = '/',
  nav,
  sidebar,
  header = {},
  footer,
  tone = 'light',
  preloaderVerse,
  bottomNav,
  mainClassName,
  seo,
  onSubscribe,
  onSearch,
}: AppShellProps) {
  const { pathname } = useLocation()
  const showPreloader = usePreloader()
  const isMobileNav = useIsMobileNav()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)

  const { cta, plainRoutes = [], actions, search = true } = header

  // Client-side navigation must reset scroll and dismiss any open overlay.
  useEffect(() => {
    setSidebarOpen(false)
    setSearchOpen(false)
    window.scrollTo(0, 0)
  }, [pathname])

  // Re-arm reveals for the newly rendered page.
  useReveal([pathname])

  // Title, description, canonical, Open Graph and structured data for this route.
  useRouteSeo(seo)

  /**
   * Does this route open into pale content? Exact match, or a `/*` entry matching the
   * prefix — see `header.plainRoutes`.
   */
  const onLight = plainRoutes.some((route) =>
    route.endsWith('/*') ? pathname.startsWith(route.slice(0, -1)) : pathname === route,
  )

  const navNode = <HeaderNav entries={nav} />

  return (
    <>
      {showPreloader && <Preloader verse={preloaderVerse} />}

      <MobileSidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        logo={logo}
        homeTo={homeTo}
        followTitle={sidebar.followTitle}
        socials={sidebar.socials}
        sites={sidebar.sites}
        sitesTitle={sidebar.sitesTitle}
        nav={isMobileNav ? navNode : null}
      />

      {search && <SearchModal open={searchOpen} onClose={() => setSearchOpen(false)} onSubmit={onSearch} />}

      <Header
        tone={tone}
        onLight={onLight}
        logo={logo}
        logoPale={logoPale}
        logoLabel={logoLabel}
        homeTo={homeTo}
        nav={isMobileNav ? null : navNode}
        cta={cta}
        actions={actions}
        showSearch={search}
        onOpenSearch={() => setSearchOpen(true)}
        onOpenSidebar={() => setSidebarOpen(true)}
      />

      {/*
        The ink header is an OVERLAY, so a page that opens with a hero reserves nothing —
        the hero is what the bar sits on, and padding there would put a dark strip above
        the photograph.

        A page that opens into content is the other case, and it takes the offset. That is
        the same distinction `plainRoutes` already draws for the light header's link
        colours, so it is read from the same list rather than from a second one that could
        disagree with it.
      */}
      <main
        className={
          [
            tone === 'ink' ? 'ul-ink-shell-main' : '',
            tone === 'ink' && onLight ? 'ul-ink-shell-main--offset' : '',
            // Room for the fixed bar, and only when there is one — see `bottomNav`.
            bottomNav ? 'ul-has-bottom-nav' : '',
            mainClassName ?? '',
          ]
            .filter(Boolean)
            .join(' ') || undefined
        }
      >
        <Outlet />
      </main>

      <Footer {...footer} tone={tone} onSubscribe={onSubscribe} />

      {bottomNav && (
        <BottomNav
          label={bottomNav.label}
          items={
            bottomNav.menuLabel
              ? [
                  ...bottomNav.items,
                  { label: bottomNav.menuLabel, icon: 'menu', onClick: () => setSidebarOpen(true) },
                ]
              : bottomNav.items
          }
        />
      )}

      <ScrollToTop />
    </>
  )
}
